import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  FileText, 
  Share2, 
  Download, 
  Loader2, 
  Trophy, 
  AlertTriangle, 
  DollarSign,
  Sparkles,
  Calendar,
  TrendingUp,
  Shirt
} from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShareCard = ({ data, reportType, shareCardRef }) => {
  if (!data) return null;

  return (
    <div 
      ref={shareCardRef}
      className="bg-gradient-to-br from-sand-50 to-white p-6 rounded-sm border border-border"
      style={{ minWidth: "340px", maxWidth: "400px" }}
    >
      <div className="space-y-5">
        {/* Header */}
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest font-semibold text-terracotta-500">
            My Wardrobe Report
          </p>
          <h3 className="font-heading text-xl text-primary mt-1">{data.headline}</h3>
        </div>

        {/* Wardrobe Value - Hero Metric */}
        <div className="text-center py-3 bg-sand-100/50 rounded-sm">
          <p className="text-xs text-muted-foreground mb-1">Total Wardrobe Value</p>
          <p className="font-heading text-4xl font-bold text-primary">
            ${data.wardrobe_value?.toLocaleString() || "0"}
          </p>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Items</p>
            <p className="font-semibold text-lg">{data.total_items || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Wears</p>
            <p className="font-semibold text-lg">{data.total_wears || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{data.key_metric?.label}</p>
            <p className="font-semibold text-lg">{data.key_metric?.value}</p>
          </div>
        </div>

        {/* Most Worn & Most Expensive Mistake */}
        <div className="grid grid-cols-2 gap-3">
          {data.most_worn_item && data.most_worn_item.brand && (
            <div className="bg-green-50 p-3 rounded-sm">
              <p className="text-[10px] uppercase tracking-wide text-green-700 font-medium mb-1">Most Worn</p>
              <p className="text-sm font-medium text-primary truncate">
                {data.most_worn_item.brand}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {data.most_worn_item.item_name}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {data.most_worn_item.period_wears} wears
              </p>
            </div>
          )}
          {data.most_expensive_mistake && data.most_expensive_mistake.brand && (
            <div className="bg-amber-50 p-3 rounded-sm">
              <p className="text-[10px] uppercase tracking-wide text-amber-700 font-medium mb-1">Biggest Mistake</p>
              <p className="text-sm font-medium text-primary truncate">
                {data.most_expensive_mistake.brand}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {data.most_expensive_mistake.item_name}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                ${data.most_expensive_mistake.cost_per_wear}/wear
              </p>
            </div>
          )}
        </div>

        {/* Style Profile */}
        <div className="text-center pt-3 border-t border-border">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">My Style</p>
          <Badge variant="secondary" className="text-xs px-3 py-1">
            <Sparkles className="w-3 h-3 mr-1" />
            {data.style_profile}
          </Badge>
        </div>

        {/* Footer */}
        <p className="text-[10px] text-muted-foreground text-center pt-2">{data.footer}</p>
      </div>
    </div>
  );
};

export function Reports() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportType, setReportType] = useState("monthly");
  const shareCardRef = useRef(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/reports`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
        if (data.length > 0) {
          setSelectedReport(data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await authFetch(`${API_URL}/api/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ report_type: reportType }),
      });

      if (response.ok) {
        const report = await response.json();
        setReports([report, ...reports]);
        setSelectedReport(report);
        toast.success("Report generated!");
      } else {
        const error = await response.clone().json().catch(() => ({}));
        if (response.status === 403) {
          toast.error("Upgrade to Core or Pro to access seasonal reports");
        } else {
          toast.error(error.detail || "Failed to generate report");
        }
      }
    } catch (error) {
      console.error("Generate error:", error);
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadImage = useCallback(async () => {
    if (!shareCardRef.current || !selectedReport) return;
    
    setDownloading(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#FDFBF7"
      });
      
      const link = document.createElement("a");
      link.download = `wornwell-report-${selectedReport.metrics?.period?.label?.replace(/\s+/g, "-") || "report"}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Report image downloaded!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download image");
    } finally {
      setDownloading(false);
    }
  }, [selectedReport]);

  const handleShare = async () => {
    if (!selectedReport) return;

    const shareData = {
      title: "My Wardrobe Report - WornWell",
      text: `Check out my wardrobe stats! Avg cost per wear: $${selectedReport.metrics?.wardrobe_value?.average_cost_per_wear}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        toast.success("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
              My Wardrobe Reports
            </h1>
            <p className="text-muted-foreground">
              Visual insights into your wardrobe performance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-44 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly Report</SelectItem>
                <SelectItem value="seasonal_fall_winter">Fall/Winter</SelectItem>
                <SelectItem value="seasonal_spring_summer">Spring/Summer</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={generateReport}
              disabled={generating}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              data-testid="generate-report-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>

        {selectedReport ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Report Selector */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Your Reports
              </h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {reports.map((report) => (
                  <button
                    key={report.report_id}
                    onClick={() => setSelectedReport(report)}
                    className={`w-full p-4 text-left rounded-sm border transition-colors ${
                      selectedReport?.report_id === report.report_id
                        ? "border-terracotta-500 bg-terracotta-500/5"
                        : "border-border hover:bg-sand-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{report.metrics?.period?.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Report Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Share Card Preview */}
              <Card className="border border-border shadow-none overflow-hidden">
                <CardHeader className="p-4 bg-sand-50 flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg">Share Card</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadImage}
                      disabled={downloading}
                      data-testid="download-report-btn"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </>
                      )}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleShare} data-testid="share-report-btn">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ShareCard 
                    data={selectedReport.share_card_data} 
                    reportType={selectedReport.report_type}
                    shareCardRef={shareCardRef}
                  />
                </CardContent>
              </Card>

              {/* Most Worn Item */}
              {selectedReport.metrics?.most_worn_item && (
                <Card className="border border-border shadow-none">
                  <CardHeader className="p-6 pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      Your Most Worn Item
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-sand-100 rounded-sm flex items-center justify-center overflow-hidden">
                        {selectedReport.metrics.most_worn_item.image_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${selectedReport.metrics.most_worn_item.image_base64}`}
                            alt="Most worn"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Shirt className="w-8 h-8 text-sand-300" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {selectedReport.metrics.most_worn_item.brand} {selectedReport.metrics.most_worn_item.item_name}
                        </p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <div>
                            <p className="text-muted-foreground">Total Wears</p>
                            <p className="font-semibold text-lg">{selectedReport.metrics.most_worn_item.total_wears}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Cost/Wear</p>
                            <p className="font-semibold text-lg text-green-600">
                              ${selectedReport.metrics.most_worn_item.cost_per_wear}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Most Expensive Mistake */}
              {selectedReport.metrics?.most_expensive_mistake && (
                <Card className="border border-red-200 shadow-none">
                  <CardHeader className="p-6 pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Your Most Expensive Mistake
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-red-50 rounded-sm flex items-center justify-center overflow-hidden">
                        {selectedReport.metrics.most_expensive_mistake.image_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${selectedReport.metrics.most_expensive_mistake.image_base64}`}
                            alt="Most expensive mistake"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Shirt className="w-8 h-8 text-red-300" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {selectedReport.metrics.most_expensive_mistake.brand} {selectedReport.metrics.most_expensive_mistake.item_name}
                        </p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <div>
                            <p className="text-muted-foreground">Purchase Price</p>
                            <p className="font-semibold text-lg">${selectedReport.metrics.most_expensive_mistake.purchase_price}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Wears</p>
                            <p className="font-semibold text-lg">{selectedReport.metrics.most_expensive_mistake.wear_count}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Cost/Wear</p>
                            <p className="font-semibold text-lg text-red-600">
                              ${selectedReport.metrics.most_expensive_mistake.cost_per_wear}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Wardrobe Value Summary */}
              <Card className="border border-border shadow-none">
                <CardHeader className="p-6 pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-terracotta-500" />
                    Your Wardrobe Value
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-sand-50 rounded-sm text-center">
                      <p className="text-xs text-muted-foreground mb-1">Total Value</p>
                      <p className="font-heading text-2xl font-bold">
                        ${selectedReport.metrics?.wardrobe_value?.total_purchase_value?.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-sand-50 rounded-sm text-center">
                      <p className="text-xs text-muted-foreground mb-1">Total Items</p>
                      <p className="font-heading text-2xl font-bold">
                        {selectedReport.metrics?.wardrobe_value?.total_items}
                      </p>
                    </div>
                    <div className="p-4 bg-sand-50 rounded-sm text-center">
                      <p className="text-xs text-muted-foreground mb-1">Total Wears</p>
                      <p className="font-heading text-2xl font-bold">
                        {selectedReport.metrics?.wardrobe_value?.total_logged_wears}
                      </p>
                    </div>
                    <div className="p-4 bg-terracotta-500/10 rounded-sm text-center">
                      <p className="text-xs text-muted-foreground mb-1">Avg Cost/Wear</p>
                      <p className="font-heading text-2xl font-bold text-terracotta-500">
                        ${selectedReport.metrics?.wardrobe_value?.average_cost_per_wear}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Style Profile */}
              <Card className="border border-border shadow-none bg-gradient-to-br from-sand-50 to-white">
                <CardHeader className="p-6 pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    Your Style Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-4">
                  <div className="text-center py-4">
                    <Badge className="bg-purple-100 text-purple-700 text-lg px-4 py-2 mb-4">
                      {selectedReport.style_profile?.profile}
                    </Badge>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                      {selectedReport.style_profile?.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="border border-border shadow-none">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-sand-300" />
              <h3 className="font-heading text-xl text-primary mb-2">No Reports Yet</h3>
              <p className="text-muted-foreground mb-6">
                Generate your first wardrobe report to see insights!
              </p>
              <Button
                onClick={generateReport}
                disabled={generating}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Generate Report
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
